import {isSpan, isTextBlock} from '@portabletext/schema'
import {isTextBlockNode} from '../engine/node/is-text-block-node'
import {
  applyInsertNodeAtPath,
  applyInsertNodeAtPoint,
} from '../internal-utils/apply-insert-node'
import {getNode} from '../traversal/get-node'
import {getParent} from '../traversal/get-parent'
import {getPathSubSchema} from '../traversal/get-path-sub-schema'
import {getSibling} from '../traversal/get-sibling'
import {parseInlineObject, parseSpan} from '../utils/parse-blocks'
import {isKeyedSegment} from '../utils/util.is-keyed-segment'
import type {OperationImplementation} from './operation.types'

export const insertChildOperationImplementation: OperationImplementation<
  'insert.child'
> = ({snapshot, operation}) => {
  const {context} = snapshot
  const focus = operation.editor.snapshot.context.selection?.focus

  if (!focus) {
    throw new Error('Unable to insert child without a focus')
  }

  const focusBlockEntry = getParent(operation.editor.snapshot, focus.path, {
    match: (node) =>
      isTextBlock({schema: operation.editor.snapshot.context.schema}, node),
  })

  if (!focusBlockEntry) {
    throw new Error('Unable to insert child without a focus block')
  }

  const focusBlock = focusBlockEntry.node

  if (!isTextBlockNode(context, focusBlock)) {
    throw new Error('Unable to insert child into a non-text block')
  }

  const focusChildSegment = focus.path.at(-1)

  if (!isKeyedSegment(focusChildSegment)) {
    throw new Error('Unable to insert child without a focus child')
  }

  const focusChildPath = [
    ...focusBlockEntry.path,
    'children',
    focusChildSegment,
  ]

  const markDefs = focusBlock.markDefs ?? []
  const markDefKeyMap = new Map<string, string>()
  for (const markDef of markDefs) {
    markDefKeyMap.set(markDef._key, markDef._key)
  }

  const schema = getPathSubSchema(snapshot, focusBlockEntry.path)

  const span = parseSpan({
    span: operation.child,
    keyGenerator: context.keyGenerator,
    markDefKeyMap,
    options: {validateFields: true},
    schema,
  })

  if (span) {
    const focusSpanEntry = getNode(operation.editor.snapshot, focusChildPath)
    const focusSpan =
      focusSpanEntry &&
      isSpan(
        {schema: operation.editor.snapshot.context.schema},
        focusSpanEntry.node,
      )
        ? focusSpanEntry.node
        : undefined

    if (focusSpan) {
      applyInsertNodeAtPoint(operation.editor, span, focus)
    } else {
      const nextSibling = getSibling(
        operation.editor.snapshot,
        focusChildPath,
        {
          direction: 'next',
        },
      )
      if (nextSibling) {
        applyInsertNodeAtPath(operation.editor, span, nextSibling.path)
      } else {
        operation.editor.apply({
          type: 'insert',
          path: focusChildPath,
          node: span,
          position: 'after',
        })
      }
    }

    // This makes sure the selection is set correctly when event handling is run
    // through the engine's Android input handling
    operation.editor.pendingSelection =
      operation.editor.snapshot.context.selection

    return
  }

  const inlineObject = parseInlineObject({
    inlineObject: operation.child,
    keyGenerator: context.keyGenerator,
    options: {validateFields: true},
    schema,
  })

  if (inlineObject) {
    const {_key, _type, ...rest} = inlineObject

    const inlineNode = {
      _key,
      _type,
      ...rest,
    }

    const focusSpanEntry = getNode(operation.editor.snapshot, focusChildPath)
    const focusSpan =
      focusSpanEntry &&
      isSpan(
        {schema: operation.editor.snapshot.context.schema},
        focusSpanEntry.node,
      )
        ? focusSpanEntry.node
        : undefined

    if (focusSpan) {
      applyInsertNodeAtPoint(operation.editor, inlineNode, focus)
    } else {
      const nextSibling = getSibling(
        operation.editor.snapshot,
        focusChildPath,
        {
          direction: 'next',
        },
      )
      if (nextSibling) {
        applyInsertNodeAtPath(operation.editor, inlineNode, nextSibling.path)
      } else {
        operation.editor.apply({
          type: 'insert',
          path: focusChildPath,
          node: inlineNode,
          position: 'after',
        })
      }
    }

    return
  }

  // The child's type is not allowed in the path's sub-schema. Noop.
}
